import React, { useState } from 'react';
import type { CustomCellEditorProps } from 'ag-grid-react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { CellEditorWrapper } from '@/components/AgGrid/Beneficiaries/components/StyledComponents';

/**
 * Inline editor for the vendor-level "Withholding Tax" column — a percentage (0–100).
 * Styled to match the Valid Until date editor (MUI TextField, no native number spinners).
 * Only valid in-range numbers propagate via `onValueChange`, so invalid input reverts on commit.
 */
const WithholdingTaxCellEditor: React.FC<CustomCellEditorProps<unknown, number | null>> = ({
  value,
  onValueChange,
  stopEditing,
}) => {
  const [text, setText] = useState(value != null ? String(value) : '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);
    if (raw === '') {
      onValueChange(null);
      return;
    }
    const parsed = Number(raw);
    // Propagate only valid 0–100 values; invalid input is ignored so the commit keeps the last valid one.
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 100) onValueChange(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') stopEditing();
    if (e.key === 'Escape') stopEditing(true);
  };

  return (
    <CellEditorWrapper>
      <TextField
        type="number"
        fullWidth
        autoFocus
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          },
          htmlInput: { min: 0, max: 100, step: 'any', inputMode: 'decimal' },
        }}
        sx={{
          '& .MuiInputBase-root': { height: 40, backgroundColor: 'background.paper' },
          // Hide the native number spinners so it reads like the date text field.
          '& input[type=number]': { MozAppearance: 'textfield' },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
        }}
      />
    </CellEditorWrapper>
  );
};

export default WithholdingTaxCellEditor;
